import { useState } from 'react';
import { Box, TextField, Button, Typography, CircularProgress } from '@mui/material';
import Modal from 'shared/Modal';
import { ModalPropsToOverride } from 'components/DynamicModalProvider/types';
import { IntegrationState } from 'types/integration';

type TransitionAction = 'suspend' | 'archive' | 'activate' | 'unarchive' | 'delete';

const ACTION_CONFIG: Record<
  TransitionAction,
  { title: string; message: string; confirmLabel: string; showReason: boolean }
> = {
  suspend: {
    title: 'Suspend Integration',
    message: 'Data sync is paused until resumed. Mosaic users may resume data sync.',
    confirmLabel: 'Suspend',
    showReason: true,
  },
  archive: {
    title: 'Archive Integration',
    message:
      'Data sync is paused until reactivated. Integration will be deleted after 90 days. Mosaic users will not see this in their Current Integrations.',
    confirmLabel: 'Archive',
    showReason: true,
  },
  activate: {
    title: 'Activate Integration',
    message: 'This will activate the integration and resume data sync.',
    confirmLabel: 'Activate',
    showReason: false,
  },
  unarchive: {
    title: 'Restore Integration',
    message: 'This will restore the integration to Suspended state.',
    confirmLabel: 'Restore',
    showReason: false,
  },
  delete: {
    title: 'Delete Integration',
    message: 'This will permanently delete the integration. This action cannot be undone.',
    confirmLabel: 'Delete',
    showReason: false,
  },
};

export const ACTION_TO_NEW_STATE: Record<Exclude<TransitionAction, 'delete'>, IntegrationState> = {
  suspend: IntegrationState.SUSPENDED,
  archive: IntegrationState.ARCHIVED,
  activate: IntegrationState.ACTIVE,
  unarchive: IntegrationState.SUSPENDED,
};

interface StateTransitionConfirmModalProps extends ModalPropsToOverride {
  action: TransitionAction;
  onConfirm: (reason?: string) => void;
  isLoading?: boolean;
}

const StateTransitionConfirmModal = ({
  isOpen,
  toggle,
  action,
  onConfirm,
  isLoading = false,
}: StateTransitionConfirmModalProps) => {
  const [reason, setReason] = useState('');
  const config = ACTION_CONFIG[action];

  const handleConfirm = () => {
    onConfirm(reason || undefined);
  };

  return (
    <Modal open={isOpen} onClose={isLoading ? undefined : toggle} modalWidth={450}>
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {config.title}
        </Typography>

        <Typography variant="body2" sx={{ mb: 3 }}>
          {config.message}
        </Typography>

        {config.showReason && (
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            size="small"
            sx={{ mb: 2 }}
          />
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            mt: 2,
          }}
        >
          <Button variant="outlined" onClick={toggle} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={action === 'delete' ? 'error' : 'primary'}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} color="inherit" /> : config.confirmLabel}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default StateTransitionConfirmModal;
